(() => {
  'use strict';
  const tuple = record => JSON.stringify([record.design, record.nativeMetal || record.metal]);
  const resolve = (records, current, axis, value) => {
    const primary = ['diamondType','shape'].find(key => new Set(records.map(record => record.selections[key]).filter(Boolean)).size > 1);
    const selectable = records.some(validGallery) ? records.filter(validGallery) : records;
    const candidates = selectable.filter(record => axis === 'metal' ? record.metal === value : record.selections[axis] === value).filter(record => !primary || axis === primary || axis === 'metal' || record.selections[primary] === current.selections[primary]);
    const score = record => (record.metal === current.metal ? 0 : 20) + Object.keys(current.selections).reduce((sum, key) => sum + (record.selections[key] === current.selections[key] ? 0 : 1), 0);
    return candidates.map((record, index) => ({ record, index, score: score(record) })).sort((a, b) => a.score - b.score || a.index - b.index)[0]?.record || null;
  };
  const validGallery = record => {
    const gallery = record?.gallery;
    const hasSourceCount = record?.sourceGalleryComplete === true;
    const sourceCount = record?.sourcePhotoCount;
    if (hasSourceCount && (!Number.isInteger(sourceCount) || sourceCount < 1 || sourceCount > 20)) return false;
    return record?.photoStatus === 'approved' && Array.isArray(gallery) && (hasSourceCount ? gallery.length === sourceCount : gallery.length >= 5) && gallery.every(image => image && /^https:\/\/cdn\.shopify\.com\//.test(image.url) && image.view && image.alt && image.width > 0 && image.height > 0) && new Set(gallery.map(image => image.url)).size === gallery.length && new Set(gallery.map(image => image.view)).size === gallery.length;
  };
  const validate = config => {
    if (config.schemaVersion !== 2 || config.experience !== 'jb-ring-catalog' || !config.records?.length) return false;
    if (new Set(config.records.map(tuple)).size !== config.records.length) return false;
    if (config.records.some(record => !record.variantId || !config.metals.includes(record.metal))) return false;
    const supportedMetals = new Set(['18K White Gold', '18K Yellow Gold', '18K Rose Gold', '18K White Gold and Yellow Gold', '18K Yellow Gold and White Gold']);
    return config.metals.length > 0 && config.metals.length <= supportedMetals.size && new Set(config.metals).size === config.metals.length && config.metals.every(metal => supportedMetals.has(metal) && config.records.some(record => record.metal === metal));
  };
  class PhotoPool {
    constructor() { this.jobs = new Map(); this.running = 0; this.tick = 0; }
    load(url, priority = 1) {
      let job = this.jobs.get(url);
      if (job) { job.priority = Math.max(job.priority, priority); job.order = ++this.tick; return job.promise; }
      job = { url, priority, order: ++this.tick, state: 'queued' };
      job.promise = new Promise((resolve, reject) => { job.resolve = resolve; job.reject = reject; });
      this.jobs.set(url, job); this.pump(); return job.promise;
    }
    pump() {
      while (this.running < 3) {
        const job = [...this.jobs.values()].filter(item => item.state === 'queued').sort((a, b) => b.priority - a.priority || b.order - a.order)[0];
        if (!job) return;
        job.state = 'loading'; this.running++;
        const image = new Image(); image.decoding = 'async';
        image.fetchPriority = job.priority > 10 ? 'high' : 'low';
        let settled = false;
        const finish = error => {
          if (settled) return;
          settled = true; clearTimeout(timer); image.onload = null; image.onerror = null; this.running--;
          if (error) { this.jobs.delete(job.url); job.reject(error); }
          else { job.state = 'ready'; job.resolve(image); }
          const ready = [...this.jobs.values()].filter(item => item.state === 'ready').sort((a, b) => b.order - a.order);
          ready.slice(20).forEach(item => this.jobs.delete(item.url));
          this.pump();
        };
        const timer = setTimeout(() => finish(new Error('timeout')), 20000);
        image.onerror = () => finish(new Error('unavailable'));
        image.onload = async () => { try { await image.decode?.(); finish(image.naturalWidth ? null : new Error('empty')); } catch (error) { finish(error); } };
        image.src = job.url;
      }
    }
    keep(urls) {
      const wanted = new Set(urls);
      for (const job of this.jobs.values()) {
        if (job.state !== 'queued' || wanted.has(job.url)) continue;
        this.jobs.delete(job.url); job.reject(new Error('superseded'));
      }
    }
  }
  const pool = new PhotoPool();
  class RingCatalog {
    constructor(root, config, labels) {
      this.root = root; this.config = config; this.labels = labels;
      this.info = root.querySelector('.product-information--inner');
      this.scope = this.info?.querySelector('variant-selects');
      this.controls = this.info?.querySelector('[data-jb-catalog-controls]');
      this.slider = root.querySelector('product-slider');
      this.gallery = root.querySelector('.product-image-container');
      if (!this.scope || !this.controls || !this.slider || !this.gallery) return;
      this.life = new AbortController(); this.revision = 0; this.committedKey = ''; this.frames = [];
      this.byId = new Map(config.records.flatMap(record => [record.variantId, ...Object.values(record.variantIdsByOrigin || {})].map(id => [String(id), record])));
      this.byOptions = new Map(config.records.map(record => [tuple(record), record]));
      const requestedId = new URL(location.href).searchParams.get('variant');
      const requestedRecord = this.byId.get(requestedId);
      this.selectedOrigin = Object.entries(requestedRecord?.variantIdsByOrigin || {}).find(([,id]) => String(id) === requestedId)?.[0] || this.nativeValues()[2] || '';
      root.dataset.jbCatalogReady = 'true';
      this.nativeFields()[0]?.classList.add('jb-catalog-native-design');
      this.status = this.controls.querySelector('[data-jb-catalog-status]');
      this.retry = this.controls.querySelector('[data-jb-catalog-retry]');
      this.empty = document.createElement('p'); this.empty.className = 'jb-catalog-photo-empty'; this.empty.hidden = true;
      this.empty.setAttribute('role','status'); this.gallery.append(this.empty);
      this.loader = document.createElement('div'); this.loader.className = 'jb-catalog-loader'; this.loader.hidden = true;
      this.loader.setAttribute('aria-hidden','true');
      const mark = document.createElement('span'); mark.className = 'jb-catalog-loader__mark'; this.loader.append(mark); this.gallery.append(this.loader);
      this.controls.addEventListener('click', event => {
        const button = event.target.closest('button[data-jb-axis]'); if (!button) return;
        const next = resolve(config.records, this.current, button.dataset.jbAxis, button.dataset.jbValue);
        if (next) this.select(next, true);
      }, { signal: this.life.signal });
      this.scope.addEventListener('change', event => {
        const field = event.target.closest('fieldset[data-index]'); if (!field) return;
        event.stopImmediatePropagation();
        const values = this.nativeValues();
        const record = this.byOptions.get(JSON.stringify(values.slice(0,2))) || resolve(config.records, this.current, 'metal', config.records.find(record => (record.nativeMetal || record.metal) === values[1])?.metal || values[1]);
        if (record?.variantIdsByOrigin && values.length > 2) this.selectedOrigin = values[2];
        if (record) this.select(record, true);
      }, { capture: true, signal: this.life.signal });
      this.retry?.addEventListener('click', () => this.requestPhotos(this.pendingRecord || this.current, this.pendingHistory), { signal: this.life.signal });
      this.root.addEventListener('click', event => {
        if (event.target.closest('.jb-request-price-button')) this.syncQuote();
      }, { capture: true, signal: this.life.signal });
      this.info.addEventListener('change', () => this.syncQuote(), { signal: this.life.signal });
      this.info.querySelector('select[data-jb-stone-origin]')?.addEventListener('change', () => this.syncQuote(), { signal: this.life.signal });
      this.controls.addEventListener('pointerover', event => this.hint(event.target), { passive: true, signal: this.life.signal });
      this.controls.addEventListener('focusin', event => this.hint(event.target), { signal: this.life.signal });
      window.addEventListener('popstate', () => {
        const id = new URL(location.href).searchParams.get('variant');
        const record = this.byId.get(id) || config.records[0];
        this.selectedOrigin = Object.entries(record.variantIdsByOrigin || {}).find(([,value]) => String(value) === id)?.[0] || this.selectedOrigin;
        this.select(record, false);
      }, { signal: this.life.signal });
      root.addEventListener('jb:experience-dispose', () => this.dispose(), { once: true, signal: this.life.signal });
      this.select(this.byId.get(new URL(location.href).searchParams.get('variant')) || config.records[0], false, true);
      this.installNative();
    }
    nativeFields() { return [...this.scope.querySelectorAll('fieldset[data-index]')].sort((a,b) => Number(a.dataset.index.replace('option','')) - Number(b.dataset.index.replace('option',''))); }
    nativeValues() { return this.nativeFields().map(field => field.querySelector('select')?.value || field.querySelector('input:checked')?.value || ''); }
    installNative() {
      customElements.whenDefined('variant-selects').then(() => {
        if (this.life.signal.aborted) return;
        this.originalRender = this.scope.renderVariantInfo;
        this.scope.renderVariantInfo = () => {
          const record = this.byOptions.get(JSON.stringify(this.nativeValues().slice(0,2)));
          if (record) this.select(record, true);
        };
        this.syncNative(this.current);
      });
    }
    variantIdFor(record) {
      const origin = this.selectedOrigin || this.info.querySelector('select[data-jb-stone-origin]')?.value || '';
      return record.variantIdsByOrigin?.[origin] || record.variantId;
    }
    skuFor(record) {
      const origin = this.selectedOrigin || this.info.querySelector('select[data-jb-stone-origin]')?.value || '';
      return record.variantSkusByOrigin?.[origin] || record.sku;
    }
    syncNative(record) {
      this.scope._variantRequest = (this.scope._variantRequest || 0) + 1;
      this.scope._variantController?.abort();
      const options = [record.design, record.nativeMetal || record.metal];
      if (record.variantIdsByOrigin && this.nativeFields().length > 2) {
        this.selectedOrigin = this.selectedOrigin || this.nativeValues()[2] || 'Lab-grown';
        options.push(this.selectedOrigin);
      }
      const variantId = this.variantIdFor(record);
      const sku = this.skuFor(record);
      this.nativeFields().forEach((field,index) => {
        const select = field.querySelector('select');
        if (select) {
          if (![...select.options].some(option => option.value === options[index])) { const option=document.createElement('option');option.value=options[index];option.textContent=options[index];select.append(option); }
          select.value = options[index];
        }
        field.querySelectorAll('input[type="radio"]').forEach(input => {
          const available = index !== 1 || !this.config.records.some(validGallery) || this.config.records.some(candidate => validGallery(candidate) && candidate.design === record.design && (candidate.nativeMetal || candidate.metal) === input.value);
          input.checked = input.value === options[index]; input.disabled = !available; input.dataset.available = String(available);
        });
        if (index === 1 && select) [...select.options].forEach(option => {
          option.disabled = this.config.records.some(validGallery) && !this.config.records.some(candidate => validGallery(candidate) && candidate.design === record.design && (candidate.nativeMetal || candidate.metal) === option.value);
        });
        if (index === 1) this.syncMetalPreview(field);
      });
      this.scope.options = options;
      this.scope.currentVariant = { id: Number(variantId), available: true };
      this.scope.fieldsets = this.nativeFields();
      this.scope.updateVariantText?.(); this.scope.updateVariantInput?.();
      this.info.querySelectorAll('input[name="id"]').forEach(input => { input.value = variantId; input.disabled = false; });
      this.info.querySelectorAll('[data-reference]').forEach(node => { node.textContent = sku; });
      this.info.querySelectorAll('[data-jb-share-state]').forEach(node => { node.dataset.variantId = variantId; node.dataset.sku = sku; });
      const selected = this.scope.querySelector('[id^="SelectedVariant-"]');
      if (selected) { selected.dataset.variantId = variantId; selected.dataset.available = 'true'; }
      this.scope.toggleAddButton?.(false, window.theme?.variantStrings?.soldOut);
      this.controls.querySelectorAll('[data-jb-axis]').forEach(button => {
        const selected = record.selections[button.dataset.jbAxis] === button.dataset.jbValue;
        button.setAttribute('aria-pressed', String(selected));
        button.disabled = !resolve(this.config.records, record, button.dataset.jbAxis, button.dataset.jbValue);
      });
      this.controls.querySelectorAll('[data-jb-axis-field]').forEach(field => { field.hidden = !record.selections[field.dataset.jbAxisField]; });
      this.syncSizes(record); this.syncOrigin(record);
    }
    syncMetalPreview(field) {
      field.querySelectorAll('.jb-metal-swatches__choice').forEach(choice => {
        const input = choice.querySelector('input[type="radio"]');
        if (!input) return;
        let help = choice.querySelector('.jb-metal-preview-help');
        let note = choice.querySelector('.jb-metal-preview-note');
        if (!help && input.disabled) {
          const metal = input.value.replace(/^18K\s+/i, '').replace(/\bGold\b/g, 'gold');
          help = document.createElement('button'); help.type = 'button'; help.className = 'jb-metal-preview-help';
          note = document.createElement('div'); note.className = 'jb-metal-preview-note'; note.hidden = true;
          note.id = input.id + '-preview-note';
          const caption = document.createElement('p'); caption.textContent = this.labels.previewUnavailable.replace('__METAL__', metal);
          const enquiry = document.createElement('a'); enquiry.href = this.labels.enquiryUrl; enquiry.textContent = this.labels.enquireMetal.replace('__METAL__', metal.toLowerCase());
          note.append(caption, enquiry); help.setAttribute('aria-label', caption.textContent); help.setAttribute('aria-controls', note.id); help.setAttribute('aria-expanded', 'false');
          choice.append(help, note);
          let pinned = false;
          const close = () => { pinned = false; note.hidden = true; help.setAttribute('aria-expanded', 'false'); };
          const open = () => {
            if (help.hidden) return;
            note.hidden = false; note.style.translate = '0px'; help.setAttribute('aria-expanded', 'true');
            const bounds = note.getBoundingClientRect();
            const shift = Math.max(16 - bounds.left, Math.min(0, window.innerWidth - 16 - bounds.right));
            note.style.translate = shift + 'px';
          };
          help.addEventListener('click', event => { event.preventDefault(); if (pinned) close(); else { pinned = true; open(); } }, {signal:this.life.signal});
          choice.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') open(); }, {signal:this.life.signal});
          choice.addEventListener('pointerleave', () => { if (!pinned && !choice.contains(document.activeElement)) close(); }, {signal:this.life.signal});
          choice.addEventListener('focusin', open, {signal:this.life.signal});
          choice.addEventListener('focusout', () => queueMicrotask(() => { if (!choice.contains(document.activeElement)) close(); }), {signal:this.life.signal});
          document.addEventListener('click', event => { if (!choice.contains(event.target)) close(); }, {signal:this.life.signal});
          choice.addEventListener('keydown', event => { if (event.key === 'Escape') { event.preventDefault(); help.focus(); close(); } }, {signal:this.life.signal});
          choice.jbClosePreview = close;
        }
        if (help) { help.hidden = !input.disabled; choice.classList.toggle('jb-metal-preview-unavailable', input.disabled); choice.jbClosePreview(); }
      });
    }
    syncSizes(record) {
      const sizes = [...(record.ringSizes || this.config.ringSizes || [])].sort((a,b) => Number.parseFloat(a) - Number.parseFloat(b));
      const select = this.info.querySelector('select[data-jb-ring-size]'); if (!select || !sizes?.length) return;
      const signature = sizes.join('|'); if (this.sizeSignature === signature) return; this.sizeSignature = signature;
      const saved = select.value;
      const placeholder = select.querySelector('option[value=""]')?.cloneNode(true);
      const unsure = select.querySelector('option[value="__jb_unsure__"]')?.cloneNode(true);
      const options = sizes.map(size => { const option=document.createElement('option');option.value='US '+size;option.textContent=option.value;return option; });
      select.replaceChildren(...[placeholder,...options,unsure].filter(Boolean));
      select.value = [...select.options].some(option=>option.value===saved) ? saved : '';
      select.jbRefinedSelectSync?.();
      this.info.querySelectorAll('.jb-first-chart-picker select').forEach(picker => {
        const saved = picker.value; const first=picker.querySelector('option[value=""]')?.cloneNode(true);
        picker.replaceChildren(...[first,...options.map(option=>option.cloneNode(true))].filter(Boolean));
        picker.value=[...picker.options].some(option=>option.value===saved)?saved:'';
        picker.dispatchEvent(new Event('change',{bubbles:true}));
      });
    }
    syncOrigin(record) {
      if (this.config.classificationReview?.originalMediaPreserved === true) return;
      const origin=this.info.querySelector('jb-stone-origin');const select=origin?.querySelector('[data-jb-stone-origin]');if(!select)return;
      const hasDiamonds=record.hasDiamonds ?? this.config.hasDiamonds;
      origin.classList.toggle('jb-catalog-origin-hidden',!hasDiamonds);
      select.disabled=!hasDiamonds;select.required=hasDiamonds;
      if(!hasDiamonds&&select.value){this.lastOrigin=select.value;select.value='';select.dispatchEvent(new Event('change',{bubbles:true}));}
      if(hasDiamonds&&!select.value&&this.lastOrigin){select.value=this.lastOrigin;select.dispatchEvent(new Event('change',{bubbles:true}));}
    }
    select(record, history, initial = false) {
      if (!record || this.life.signal.aborted) return;
      if (!validGallery(record)) {
        const ready = this.config.records.filter(validGallery);
        const fallback = (validGallery(this.current) && this.current) || ready.find(candidate => candidate.design === record.design) || ready[0];
        if (fallback) { record = fallback; history = true; }
      }
      if (this.zoom?.open) this.zoom.close();
      if (validGallery(record)) {
        if (!this.current || !this.committedKey) this.commitSelection(record, history);
        else this.syncNative(this.current);
        if (initial) this.retainInitialFrames(record);
        this.requestPhotos(record, history);
      } else {
        this.commitSelection(record, history);
        ++this.revision;
        pool.keep([]); this.hideGallery(this.labels.pending);
        this.setStatus(this.labels.pending, false, false);
      }
    }
    commitSelection(record, history) {
      this.current = record; this.syncNative(record); this.syncDetails(); this.syncQuote();
      if (history) {
        const url = new URL(location.href); url.searchParams.set('variant', this.variantIdFor(record)); window.history.replaceState({}, '', url);
      }
    }
    syncDetails() {
      const details = this.info.querySelector('[data-jb-catalog-details]'); if (!details) return;
      const facts = details.querySelector('dl'); if (!facts) return;
      const fragment = document.createDocumentFragment();
      const entries = Object.entries(this.current.specs);
      if (this.current.hasDiamonds ?? this.config.hasDiamonds) entries.push([this.labels.origin, this.info.querySelector('select[data-jb-stone-origin]')?.value || this.labels.chooseOrigin]);
      for (const [label,value] of entries) {
        const row = document.createElement('div'); const dt = document.createElement('dt'); const dd = document.createElement('dd');
        dt.textContent = label; dd.textContent = value; row.append(dt,dd); fragment.append(row);
      }
      facts.replaceChildren(fragment);
    }
    syncQuote() {
      if (!this.current) return;
      this.syncDetails();
      const form = document.getElementById('RequestPriceForm'); if (!form) return;
      const origin = this.info.querySelector('select[data-jb-stone-origin]')?.value || this.selectedOrigin || this.nativeValues()[2] || '';
      const options = this.config.axes.filter(axis => this.current.selections[axis.key]).map(axis => axis.label + ': ' + (axis.values.find(item => item.value === this.current.selections[axis.key])?.label || '')).concat(['Metal: ' + this.current.metal]);
      const values = { 'Variant ID': this.variantIdFor(this.current), SKU: this.skuFor(this.current), 'Selected options': options.join('; '), 'Stone origin': origin || '', 'Origin applies to': (this.current.hasDiamonds ?? this.config.hasDiamonds) ? this.labels.originScope : '' };
      for (const [name,value] of Object.entries(values)) {
        let input = [...form.querySelectorAll('input')].find(node => node.name === 'contact[' + name + ']');
        if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = 'contact[' + name + ']'; form.append(input); }
        input.value = value; input.disabled = !value;
      }
    }
    setStatus(message, retry, busy = true) {
      if (this.status) { this.status.textContent = message; this.status.hidden = !message; this.status.classList.toggle('visually-hidden', !retry && busy); }
      if (this.retry) this.retry.hidden = !retry;
      this.slider.setAttribute('aria-busy', String(!!message && !retry && busy));
    }
    hideGallery(message) {
      this.animation?.cancel(); this.photoCover?.remove(); this.zoom?.close();
      this.root.dataset.jbCatalogVisible = 'false'; this.slider.inert = true; this.slider.setAttribute('aria-hidden','true');
      if (message === this.labels.updating) {
        this.empty.hidden = true; this.startLoader();
      } else {
        this.stopLoader(true); this.empty.textContent = message; this.empty.hidden = false;
      }
    }
    startLoader() {
      clearTimeout(this.loaderTimer); clearTimeout(this.loaderHideTimer);
      this.loader.hidden = false; this.loader.dataset.visible = 'false';
      this.loaderTimer = setTimeout(() => {
        if (!this.life.signal.aborted && this.root.isConnected && this.root.dataset.jbCatalogVisible !== 'true') this.loader.dataset.visible = 'true';
      }, 300);
    }
    stopLoader(immediate = false) {
      clearTimeout(this.loaderTimer); clearTimeout(this.loaderHideTimer);
      const visible = this.loader.dataset.visible === 'true';
      this.loader.dataset.visible = 'false';
      if (immediate || !visible || matchMedia('(prefers-reduced-motion: reduce)').matches) this.loader.hidden = true;
      else this.loaderHideTimer = setTimeout(() => { this.loader.hidden = true; }, 180);
    }
    showGallery() {
      this.root.dataset.jbCatalogVisible = 'true'; this.slider.inert = false; this.slider.removeAttribute('aria-hidden'); this.empty.hidden = true;
      this.stopLoader();
    }
    retainInitialFrames(record) {
      const slides = [...this.slider.querySelectorAll('.product-images__slide')];
      if (slides.length !== record.gallery.length) return false;
      const same = slides.every((slide,index) => {
        const img = slide.querySelector('img'); if (!img) return false;
        const existing = new URL(img.currentSrc || img.src,location.href); const target = new URL(record.gallery[index].url);
        return existing.pathname === target.pathname;
      });
      if (!same) return false;
      this.frames = slides; return true;
    }
    activateGallery() {
      this.root.dataset.jbCatalogGallery = 'true';
      this.showGallery();
      this.slider.dataset.hideVariants = 'false'; this.slider.setAttribute('aria-busy','false');
      if (this.slider.jbCatalogGuard) return;
      this.slider.jbCatalogGuard = true;
      this.slider.openPhotoSwipe = () => {};
      this.slider.pswp?.close();
      this.slider.addEventListener('click', event => {
        const anchor = event.target.closest('.product-single__media-zoom'); if (!anchor) return;
        event.preventDefault(); event.stopImmediatePropagation();
        const index = this.frames.findIndex(frame => frame.contains(anchor)); this.openZoom(Math.max(0,index));
      }, { capture:true, signal:this.life.signal });
    }
    makeFrames(count) {
      if (this.frames.length === count) return;
      const originals = [...this.slider.querySelectorAll('.product-images__slide')];
      let template = originals.find(slide => slide.querySelector('img'));
      if (!template) {
        template = document.createElement('div'); template.className = 'product-images__slide';
        const media = document.createElement('div'); media.className = 'product-single__media';
        const image = document.createElement('img'); const anchor = document.createElement('a'); anchor.className = 'product-single__media-zoom';
        media.append(image,anchor); template.append(media);
      }
      this.frames = Array.from({length:count},(_,index) => {
        const frame = template.cloneNode(true);
        frame.id = 'Slide-JBCatalog-' + this.config.productId + '-' + index;
        frame.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
        frame.querySelectorAll('video, iframe, model-viewer, deferred-media').forEach(node => node.remove());
        frame.hidden = false; frame.removeAttribute('aria-hidden'); frame.classList.add('is-active');
        return frame;
      });
      const fragment = document.createDocumentFragment(); this.frames.forEach(frame => fragment.append(frame));
      originals.forEach(frame => frame.remove()); this.slider.prepend(fragment);
    }
    cover() {
      if (window.JBProductGalleryMotion) return window.JBProductGalleryMotion.capture(this.gallery, this.frames);
      const cover = document.createElement('div'); cover.className = 'jb-catalog-photo-cover'; cover.inert = true; cover.setAttribute('aria-hidden','true');
      const left = this.slider.scrollLeft;
      this.frames.forEach(frame => {
        const img = frame.querySelector('img'); const clone = document.createElement('div'); clone.className = 'jb-catalog-photo-cover__frame';
        if (img) { const copy = img.cloneNode(false); copy.alt = ''; copy.removeAttribute('id'); copy.loading = 'eager'; const computed = getComputedStyle(img); Object.assign(copy.style, { boxSizing: computed.boxSizing, padding: computed.padding, objectFit: computed.objectFit, objectPosition: computed.objectPosition }); clone.append(copy); }
        cover.append(clone);
      });
      cover.style.width = this.slider.clientWidth + 'px';
      cover.style.top = this.slider.offsetTop + 'px'; cover.style.left = this.slider.offsetLeft + 'px';
      if (innerWidth < 768) { cover.style.height = this.slider.clientHeight + 'px'; cover.style.setProperty('--gallery-offset', -left + 'px'); }
      this.gallery.append(cover); return cover;
    }
    captureGalleryPosition() {
      if (!this.committedKey || this.root.dataset.jbCatalogVisible !== 'true') return null;
      if (matchMedia('(max-width: 767px)').matches) {
        const viewport = this.slider.getBoundingClientRect();
        let index = 0; let distance = Infinity;
        this.frames.forEach((frame, candidate) => {
          const box = frame.getBoundingClientRect();
          const next = Math.abs((box.left + box.right) / 2 - (viewport.left + viewport.right) / 2);
          if (box.width > 0 && next < distance) { index = candidate; distance = next; }
        });
        const committed = this.config.records.find(record => record.key === this.committedKey);
        return { index, view: committed?.gallery[index]?.view, horizontal: true };
      }
      const height = window.innerHeight || document.documentElement.clientHeight;
      let index = -1; let visibleHeight = 0;
      this.frames.forEach((frame, candidate) => {
        const box = frame.getBoundingClientRect();
        const overlap = Math.max(0, Math.min(height, box.bottom) - Math.max(0, box.top));
        if (box.height > 0 && overlap > visibleHeight) { index = candidate; visibleHeight = overlap; }
      });
      if (index < 0) return null;
      const committed = this.config.records.find(record => record.key === this.committedKey);
      return { index, view: committed?.gallery[index]?.view, top: this.frames[index].getBoundingClientRect().top, x: window.scrollX };
    }
    restoreGalleryPosition(position, record) {
      if (!position) return;
      const matching = record.gallery.findIndex(image => image.view === position.view);
      const index = matching >= 0 ? matching : Math.min(position.index, this.frames.length - 1);
      const frame = this.frames[index]; if (!frame) return;
      if (position.horizontal) {
        const left = frame.getBoundingClientRect().left - this.slider.getBoundingClientRect().left + this.slider.scrollLeft;
        this.slider.scrollTo({ left, behavior: 'instant' });
        return;
      }
      const delta = frame.getBoundingClientRect().top - position.top;
      if (Math.abs(delta) > .5) window.scrollTo({ left: position.x, top: window.scrollY + delta, behavior: 'instant' });
    }
    async requestPhotos(record, history = false) {
      const revision = ++this.revision;
      this.pendingRecord = record; this.pendingHistory = history;
      if (!validGallery(record)) { pool.keep([]); this.hideGallery(this.labels.pending); this.setStatus(this.labels.pending,false,false); return; }
      const count = record.gallery.length;
      if (record.key === this.committedKey) { pool.keep(record.gallery.map(image => image.url)); this.commitSelection(record, history); this.showGallery(); this.setStatus('',false); return; }
      if (!this.committedKey) this.hideGallery(this.labels.updating);
      this.setStatus(this.labels.updating, false);
      const urls = record.gallery.map(image => image.url); pool.keep(urls);
      try {
        const position = this.captureGalleryPosition();
        const matching = record.gallery.findIndex(image => image.view === position?.view);
        const visibleIndex = matching >= 0 ? matching : Math.min(position?.index || 0, count - 1);
        await Promise.all([...new Set([urls[0], urls[visibleIndex]])].map(url => pool.load(url,1000+revision)));
        if (revision !== this.revision || !this.root.isConnected || this.life.signal.aborted) return;
        await new Promise(requestAnimationFrame);
        if (revision !== this.revision || !this.root.isConnected || this.life.signal.aborted) return;
        if (!validGallery(record) || record.gallery.length !== count || urls.some((url,index) => record.gallery[index].url !== url)) throw new Error('changed gallery');
        const galleryPosition = this.captureGalleryPosition();
        this.animation?.cancel(); this.photoCover?.remove();
        this.makeFrames(count);
        if (this.frames.length !== count) throw new Error('missing gallery');
        this.frames.forEach((frame,index) => {
          const image = frame.querySelector('img'); const data = record.gallery[index];
          frame.hidden = false; frame.removeAttribute('aria-hidden'); frame.dataset.jbCatalogView = data.view;
          image.removeAttribute('srcset'); image.removeAttribute('data-srcset'); image.removeAttribute('data-src'); image.removeAttribute('sizes');
          image.loading = index === 0 ? 'eager' : 'lazy'; image.fetchPriority = index === 0 ? 'high' : 'low'; image.decoding = 'async';
          image.src = data.url; image.alt = data.alt; image.width = data.width; image.height = data.height;
          image.classList.remove('lazyload','lazyloading'); image.classList.add('lazyloaded','no-blur');
          const anchor = frame.querySelector('.product-single__media-zoom');
          if (anchor) { anchor.href = data.url; anchor.title = data.alt; anchor.dataset.index = String(index); anchor.dataset.msrc = data.url; anchor.dataset.thumbSrc = data.url; anchor.dataset.w = String(data.width); anchor.dataset.h = String(data.height); anchor.setAttribute('aria-label',data.alt); }
          frame.querySelectorAll('.figcaption').forEach(caption => { caption.textContent = data.alt; });
        });
        this.commitSelection(record, history);
        this.activateGallery();
        this.slider.onPaginationResize?.();
        this.restoreGalleryPosition(galleryPosition, record);
        this.committedKey = record.key; this.root.dataset.jbCatalogRendered = record.key;
        this.setStatus('', false);
        urls.slice(1).forEach(url => { pool.load(url,10+revision).catch(()=>{}); });
      } catch (error) {
        if (revision !== this.revision || this.life.signal.aborted) return;
        if (this.committedKey) this.showGallery(); else this.hideGallery(this.labels.failed);
        this.setStatus(this.labels.failed,true,false);
      }
    }
    hint(target) {
      if (navigator.connection?.saveData || this.slider.getAttribute('aria-busy') === 'true') return;
      const button = target.closest('[data-jb-axis]'); if (!button) return;
      const record = resolve(this.config.records,this.current,button.dataset.jbAxis,button.dataset.jbValue);
      if (record && validGallery(record)) pool.load(record.gallery[0].url,1).catch(()=>{});
    }
    openZoom(index) {
      if (this.root.dataset.jbCatalogVisible !== 'true' || this.current.key !== this.committedKey) return;
      const record = this.config.records.find(item => item.key === this.committedKey); if (!record) return;
      if (!this.zoom) {
        const dialog = document.createElement('dialog'); dialog.className = 'jb-catalog-zoom';
        dialog.setAttribute('aria-label', this.root.querySelector('h1')?.textContent?.trim() || record.gallery[0].alt);
        const close = document.createElement('button'); close.type = 'button'; close.className = 'jb-catalog-zoom__close'; close.textContent = this.labels.close;
        const image = document.createElement('img'); image.decoding = 'async';
        const navigation = document.createElement('div'); navigation.className = 'jb-catalog-zoom__nav';
        const previous = document.createElement('button'); previous.type = 'button'; previous.textContent = this.labels.previous;
        const next = document.createElement('button'); next.type = 'button'; next.textContent = this.labels.next;
        const count = document.createElement('span'); count.setAttribute('aria-live','polite');
        navigation.append(previous,count,next); dialog.append(close,image,navigation); this.root.append(dialog);
        close.addEventListener('click',()=>dialog.close());
        previous.addEventListener('click',()=>this.showZoom(this.zoomIndex-1)); next.addEventListener('click',()=>this.showZoom(this.zoomIndex+1));
        dialog.addEventListener('keydown',event=>{if(event.key==='ArrowLeft')this.showZoom(this.zoomIndex-1);if(event.key==='ArrowRight')this.showZoom(this.zoomIndex+1);});
        dialog.addEventListener('click',event=>{if(event.target===dialog)dialog.close();});
        this.zoom = dialog;
      }
      this.zoomRecord = record; this.showZoom(index); this.zoom.showModal();
    }
    showZoom(index) {
      const count = this.zoomRecord.gallery.length;
      this.zoomIndex = ((index%count)+count)%count; const data = this.zoomRecord.gallery[this.zoomIndex]; const img = this.zoom.querySelector('img');
      img.src = data.url; img.alt = data.alt; img.width = data.width; img.height = data.height;
      this.zoom.querySelector('[aria-live]').textContent = (this.zoomIndex+1)+' / '+count;
    }
    dispose() { ++this.revision; this.life.abort(); this.stopLoader(true); this.loader?.remove(); this.animation?.cancel(); this.photoCover?.remove(); this.zoom?.remove(); this.empty?.remove(); if(this.originalRender)this.scope.renderVariantInfo=this.originalRender; }
  }
  if (typeof module !== 'undefined' && module.exports) { module.exports = { resolve, validGallery, validate, tuple, RingCatalog, PhotoPool }; return; }
  const mount = () => {
    document.querySelectorAll('script[data-jb-ring-catalog-manifest]').forEach(node => {
      const root = document.querySelector('.jb-product-experience[data-handle="'+node.dataset.handle+'"]'); if (!root || root.dataset.jbCatalogReady) return;
      try { const {configuration,labels}=JSON.parse(node.textContent); if(validate(configuration))new RingCatalog(root,configuration,labels); } catch(error) { root.dataset.jbCatalogError = 'configuration'; }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',mount,{once:true}); else mount();
  document.addEventListener('shopify:section:load',mount);
})();








